// React
import { useState } from "react";

// External libraries
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboard, faClipboardCheck, faSparkles, faExclamationCircle } from "@fortawesome/sharp-solid-svg-icons";

// Internal
import { useAiChatContext } from "../ai-chat-context";
import { Button } from "../buttons/button";
import { getDataTestId } from "../functions/data-test-ids";
import { useOptionalHelpContext } from "../help-context";
import { useOverflowObserver } from "../hooks/useOverflowObserver.jsx";
import { LabelTooltip, Tooltip } from "../tooltip/tooltip";

const DefaultRequiredStringIndicator = " *";

function AiPromptButtons(props) {
    const aiChatContext = useAiChatContext();

    const setChatContextPrompt = (data) => {
        let value = {
            Prompt: data.Prompt
        }
        if (data.AttachRecord) {
            value.Attachment = props.Value;
        }
        aiChatContext.submit(value);
    };

    const getMenuItems = () => {
        if (!props.Buttons || props.Buttons.length === 0) {
            return [];
        }
        let menuItems = [];
        for (let i = 0, len = props.Buttons.length; i < len; i++) {
            menuItems.push({
                Label: props.Buttons[i].Label,
                Icon: props.Buttons[i].Icon,
                Data: props.Buttons[i].Data
            });
        }
        return menuItems;
    };

    if (!props.Buttons || props.Buttons.length === 0) {
        return null;
    }

    if (props.Buttons.length === 1) {
        return (
            <Button
                HideLabel={props.HideLabel}
                Label="AI assistant"
                Icon={faSparkles}
                Data={props.Buttons[0].Data}
                OnClick={setChatContextPrompt}
                DataTestId={props.DataTestId} />
        );
    }

    return (
        <Button
            HideLabel={props.HideLabel}
            Label="AI assistant"
            Icon={faSparkles}
            HideDropDownIndicator
            MenuItems={getMenuItems()}
            OnClick={setChatContextPrompt}
            DataTestId={props.DataTestId} />
    );
}

function FormInputCopyToClipboardButton(props) {
    const [copied, setCopied] = useState(false);

    const updateCopied = () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 5000);
    };

    const copyToClipboard = async () => {
        try {
            await props.Copy?.();
            updateCopied();
        } catch {
            // Copy failed; don't show success feedback.
        }
    };

    if (!props.Visible) {
        return null;
    }

    return (
        <Button
            HideLabel={props.HideLabel}
            Label="Copy to clipboard"
            Icon={copied ? faClipboardCheck : faClipboard}
            OnClick={copyToClipboard}
            DataTestId={getDataTestId(props.DataTestId, "copy-to-clipboard")} />
    );
}

function HelpLinkButton(props) {
    const helpContext = useOptionalHelpContext();

    const navigateToHelp = () => {
        helpContext.push(props.HelpLink);
    };

    // No provider means the host has nowhere to show an article, so offer no link.
    if (!helpContext || !props.HelpLink || !props.HelpLink.Type || !props.HelpLink.Id) return null;
    return (
        <button type="button" className="eone-ui-form-input-button" onClick={navigateToHelp} data-testid={getDataTestId(props.DataTestId, "help")}>
            {props.HelpLabel}
        </button>
    );
}

function FormInputError(props) {
    if (!props.Error) {
        return null;
    }

    return (
        <label className="eone-ui-form-error" data-testid={getDataTestId(props.DataTestId, "error")}>
            <FontAwesomeIcon icon={faExclamationCircle} />
            {props.Error}
        </label>
    );
}

function FormInputLabel(props) {
    const { containerRef, getOverflow } = useOverflowObserver(1);

    const useOverflowHook = () => {
        if (!props.CollapsibleButtons) return false;

        let count = props.Buttons?.length ?? 0;
        if (count > 1) return true;

        if (props.ShowCopyButton) count++;
        if (props.AiPromptButtons?.length > 0) count++;
        if (count > 1) return true;
        return false;
    };

    const getButtons = (overflow) => {
        if (props.Buttons === undefined || props.Buttons === null || props.Buttons.length === 0) {
            return null;
        }

        return props.Buttons.map((button, index) => {
            return (
                <Button
                    HideLabel={overflow}
                    key={button.Id ?? button.Label ?? index}
                    Id={button.Id}
                    Label={button.Label}
                    Title={button.Title ?? button.Label}
                    Icon={button.Icon}
                    IconStyle={button.IconStyle}
                    MenuItems={button.MenuItems}
                    Style={button.Style}
                    Data={button.Data}
                    OnClick={button.OnClick}
                    DataTestId={getDataTestId(props.DataTestId, button.Label)} />
            );
        });
    };

    if (props.HideLabel) {
        return null;
    }

    const label = props.ShowRequiredIndicator ? props.Label + (props.RequiredStringIndicator || DefaultRequiredStringIndicator) : props.Label;
    const overflow = getOverflow(0) ?? false;

    return (
        <div className="eone-ui-form-label-container" ref={useOverflowHook() ? containerRef : undefined}>
            <label className="eone-ui-form-label" htmlFor={props.Id} data-testid={getDataTestId(props.DataTestId, "label")}>
                <LabelTooltip Tooltip={props.Tooltip}>
                    <span className={props.HeaderClass}>{label}</span>
                </LabelTooltip>
                <Tooltip
                    Title={props.TooltipTitle || props.Label}
                    Tooltip={props.Tooltip}
                    TooltipWidth={props.TooltipWidth}
                    DataTestId={props.DataTestId} />
            </label>
            <div className="eone-ui-form-label-button-container">
                {getButtons(overflow)}
                <FormInputCopyToClipboardButton
                    HideLabel={overflow}
                    Visible={props.ShowCopyButton}
                    Copy={props.Copy}
                    DataTestId={props.DataTestId} />
                <HelpLinkButton
                    HelpLabel={props.HelpLabel}
                    HelpLink={props.HelpLink}
                    DataTestId={props.DataTestId} />
                <AiPromptButtons
                    HideLabel={overflow}
                    Value={props.children?.props?.Value}
                    Buttons={props.AiPromptButtons}
                    DataTestId={props.DataTestId} />
            </div>
        </div>
    );
}

function FormInput(props) {
    const getContainerStyle = () => {
        let style = props.Style || {};
        if (props.Grow) {
            style.flexGrow = 1;
            style.minHeight = 0;
        }
        if (props.NoPadding) {
            style.padding = "0px";
        }
        return style;
    };

    if (props.Visible !== undefined && !props.Visible) {
        return null;
    }

    return (
        <div className={`eone-ui-form-group ${props.Class ? props.Class : ""}`} style={getContainerStyle()}>
            <FormInputLabel
                HideLabel={props.HideLabel}
                DataTestId={props.DataTestId}
                AiPromptButtons={props.AiPromptButtons}
                HelpLink={props.HelpLink}
                HelpLabel={props.HelpLabel}
                Copy={props.Copy}
                ShowCopyButton={props.ShowCopyButton}
                TooltipWidth={props.TooltipWidth}
                Tooltip={props.Tooltip}
                TooltipTitle={props.TooltipTitle}
                Label={props.Label}
                HeaderClass={props.HeaderClass}
                Id={props.Id}
                ShowRequiredIndicator={props.ShowRequiredIndicator}
                RequiredStringIndicator={props.RequiredStringIndicator}
                Buttons={props.Buttons}
                CollapsibleButtons={props.CollapsibleButtons} />
            {props.children}
            <FormInputError
                Error={props.Error}
                DataTestId={props.DataTestId} />
        </div>
    );
}

export { FormInput };
